import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ChatCircleDots, PaperPlaneRight, Paperclip, X, ArrowsClockwise } from "@phosphor-icons/react";

const MAX_FILE = 5 * 1024 * 1024;
function readFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ name: file.name, data: r.result, type: file.type });
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
const timeFmt = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function LeadMessages({ leadId, clientEmail = "", clientName = "" }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [to, setTo] = useState(clientEmail);
  const [file, setFile] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { setTo(clientEmail); }, [clientEmail]);

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["lead-messages", leadId],
    queryFn: () => api.leadMessages(leadId),
    refetchInterval: 20000,
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = useMutation({
    mutationFn: async () => {
      let attachment = null;
      if (file) attachment = { name: file.name, data: file.data };
      return api.sendLeadMessage(leadId, { body: text.trim(), to_email: to.trim(), attachment });
    },
    onSuccess: (res) => {
      setText(""); setFile(null);
      qc.invalidateQueries({ queryKey: ["lead-messages", leadId] });
      if (res.emailed) toast.success("Message sent & emailed to client");
      else if (!res.email_configured) toast.message("Logged. Email isn't set up yet on the server.");
      else toast.message("Logged. Email not delivered (test mode until your domain is verified).");
    },
    onError: (e) => toast.error(errMsg(e, "Could not send message")),
  });

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE) { toast.error("File must be under 5MB"); return; }
    setFile(await readFile(f));
  };

  const canSend = text.trim().length > 0 && !send.isPending;

  return (
    <div className="border border-[#111827] flex flex-col" data-testid="lead-messages" style={{ minHeight: 420 }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-2">
          <ChatCircleDots size={18} weight="bold" color="#002FA7" />
          <span className="font-semibold text-[#111827]">Client conversation</span>
        </div>
        <button data-testid="messages-refresh" onClick={() => qc.invalidateQueries({ queryKey: ["lead-messages", leadId] })}
          className="text-[#4B5563] hover:text-[#002FA7] transition-colors"><ArrowsClockwise size={16} weight="bold" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]" style={{ maxHeight: 360 }} data-testid="messages-list">
        {isLoading && <p className="text-sm text-[#9CA3AF]">Loading…</p>}
        {!isLoading && messages.length === 0 && (
          <p className="text-sm text-[#9CA3AF]" data-testid="messages-empty">No messages yet. Send one below — it emails the client and is logged here.</p>
        )}
        {messages.map((m) => {
          const out = m.direction === "out";
          return (
            <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[78%] px-3 py-2 text-sm ${out ? "bg-[#002FA7] text-white" : "bg-white border border-[#E5E7EB] text-[#111827]"}`}>
                <div className="whitespace-pre-line">{m.body}</div>
                {m.attachment?.name && (
                  <div className={`mt-1 inline-flex items-center gap-1 text-xs ${out ? "text-[#C7D2FE]" : "text-[#4B5563]"}`}>
                    <Paperclip size={12} weight="bold" /> {m.attachment.name}
                  </div>
                )}
                <div className={`text-[10px] mt-1 ${out ? "text-[#C7D2FE]" : "text-[#9CA3AF]"}`}>
                  {out ? (m.from_name || "You") : (m.from_email || "Client")} · {timeFmt(m.created_at)}{out && (m.emailed ? " · emailed" : " · logged")}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="border-t border-[#E5E7EB] p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#4B5563] shrink-0">To</span>
          <input data-testid="message-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
            placeholder="client email" className="flex-1 border border-[#E5E7EB] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        {file && (
          <div className="flex items-center gap-2 text-xs text-[#4B5563]">
            <Paperclip size={12} weight="bold" /> {file.name}
            <button onClick={() => setFile(null)} className="text-[#DC2626]" data-testid="message-file-clear"><X size={12} weight="bold" /></button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea data-testid="message-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Type a message to the client…" className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          <label className="cursor-pointer border border-[#E5E7EB] p-2.5 hover:border-[#002FA7] transition-colors" data-testid="message-attach">
            <Paperclip size={16} weight="bold" color="#4B5563" />
            <input type="file" className="hidden" onChange={pickFile} />
          </label>
          <button data-testid="message-send" disabled={!canSend} onClick={() => send.mutate()}
            className="bg-[#002FA7] text-white p-2.5 hover:bg-[#00227A] disabled:opacity-50 transition-colors active:scale-95">
            <PaperPlaneRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
